import { describeAge, MARKET_STALE_DAYS, type LotMarket } from './sell-through';

/**
 * Working out what to ask for a lot, from what it cost you and from what BrickLink says is happening to it
 * (what it has been selling for, what is listed now, and how fast it moves). It suggests a handful of prices with
 * the reasoning behind each; choosing one is up to the person.
 *
 * All figures are before BrickLink's fees and postage.
 */

/** The lowest margin the "floor" price allows: profit as a share of the price. */
export const FLOOR_MARGIN = 0.2;
/** The margin the "cost plus" price aims for. */
export const TARGET_MARGIN = 0.5;
/** How far under recent sales the "quick sale" price goes. */
export const QUICK_DISCOUNT = 0.1;
/** The most the "patient" price goes above recent sales. */
export const PATIENT_CEILING = 1.25;
/** Fewer sales than this and the average is rough. */
export const FEW_SALES = 5;

export type PriceOptionKey = 'breakeven' | 'floor' | 'cost_plus' | 'quick' | 'market' | 'patient';

export interface PriceOption {
  key: PriceOptionKey;
  label: string;
  /** A decimal string, four places, rounded to a sensible step. */
  price: string;
  /** Price less cost. Null when the cost is not known. */
  profitEach: string | null;
  /** Profit as a share of the price, in percent. Null when the cost is not known. */
  marginPct: number | null;
  /** How far the price is above (+) or below (-) what it has been selling for, in percent. Null without sales figures. */
  vsSoldPct: number | null;
  /** Why this price. */
  basis: string;
  warnings: string[];
  recommended: boolean;
}

export interface PriceInput {
  /** Average cost of what is in stock. Null when there is none, or none with a known cost. */
  avgCost: number | null;
  /** Pieces in stock whose cost is not known, which are not in the average. */
  unknownCostQty: number;
  /** Pieces in stock. */
  qty: number;
  /** The price now, if there is one. */
  current: number | null;
  /** The currency the BrickLink figures are in, and the one you work in. Figures in another currency are not used. */
  marketCurrency: string | null;
  currency: string;
  market: LotMarket | null;
  now?: Date;
}

export interface PriceSuggestions {
  options: PriceOption[];
  /** Things worth knowing before choosing: thin data, stale figures, no cost, and so on. */
  notes: string[];
  facts: {
    avgCost: string | null;
    soldAvg: string | null;
    soldMin: string | null;
    soldMax: string | null;
    soldPieces: number | null;
    soldLots: number | null;
    forSaleAvg: string | null;
    forSaleMin: string | null;
    forSalePieces: number | null;
    sellThroughPct: number | null;
    soldPerMonth: number | null;
    /** How long what you hold would last at the market's pace, in months. */
    yourMonthsOfStock: number | null;
    marketAge: string | null;
    /** The BrickLink figures are older than MARKET_STALE_DAYS. */
    stale: boolean;
    current: string | null;
  };
}

const num = (s: string | null | undefined): number | null => {
  if (s == null) return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Rounds to whole pennies (never under 1p), and to a neater step as the price grows. */
export function nicePrice(p: number): number {
  const step = p < 10 ? 0.01 : p < 100 ? 0.25 : 1;
  return Math.max(0.01, Math.round(p / step) * step);
}

const money4 = (n: number) => n.toFixed(4);

const SYMBOLS: Record<string, string> = { GBP: '£', EUR: '€', USD: '$' };
/** For sentences: £0.10, and the places a per-piece figure needs when it has them (£0.0471). */
export function shown(n: number, currency: string): string {
  let t = n.toFixed(4).replace(/0+$/, '');
  const dot = t.indexOf('.');
  if (dot === -1) t += '.00';
  else if (t.length - dot - 1 < 2) t = t.padEnd(dot + 3, '0');
  return `${SYMBOLS[currency.toUpperCase()] ?? ''}${t}`;
}

export function suggestPrices(i: PriceInput): PriceSuggestions {
  const $ = (n: number) => shown(n, i.currency);
  const notes: string[] = [];
  const cost = i.avgCost !== null && i.avgCost > 0 ? i.avgCost : null;
  if (cost === null) {
    notes.push(
      i.avgCost === 0
        ? 'This stock cost nothing (it was added without a purchase price), so any price is all profit. The market figures are what to go on.'
        : 'There is no stock of this, so there is no cost to work from.',
    );
  }

  const m = i.market;
  const sameCurrency = !i.marketCurrency || i.marketCurrency.toUpperCase() === i.currency.toUpperCase();
  const usable = !!m && m.status !== 'unavailable' && sameCurrency;
  if (m && m.status === 'unavailable') notes.push('BrickLink has no price guide for this combination, so only cost-based prices are offered.');
  else if (m && !sameCurrency) notes.push(`BrickLink's figures are in ${i.marketCurrency}, not ${i.currency}, so they are not used.`);
  else if (!m) notes.push('No BrickLink figures are held for this lot yet, so only cost-based prices are offered. Refresh them from BrickLink to see market prices.');
  else if (m.status === 'pending') notes.push('Only one of BrickLink\'s two price guides is held so far, so the sell-through is not known yet.');

  const sold = usable ? m!.sold : null;
  const forSale = usable ? m!.forSale : null;
  const soldAvg = sold ? (num(sold.qtyAvg) ?? num(sold.avg)) : null;
  const soldMin = sold ? num(sold.min) : null;
  const soldMax = sold ? num(sold.max) : null;
  const stockAvg = forSale ? (num(forSale.qtyAvg) ?? num(forSale.avg)) : null;
  const stockMin = forSale ? num(forSale.min) : null;
  const measure = usable ? m!.measure : null;

  if (usable && sold && sold.lots !== null && sold.lots < FEW_SALES) notes.push(`Only ${sold.lots} sale${sold.lots === 1 ? '' : 's'} in the last 6 months, so the selling price is rough.`);
  if (usable && m && (i.now ?? new Date()).getTime() - new Date(m.fetchedAt).getTime() > MARKET_STALE_DAYS * 86_400_000) {
    notes.push(`The BrickLink figures are ${describeAge(m.fetchedAt, i.now)} and may be out of date.`);
  }
  if (usable && m && m.region !== 'worldwide') notes.push(`The figures count sales in ${m.region === 'uk' ? 'the UK' : 'Europe'} only.`);
  if (usable && sold && soldAvg === null) notes.push('Nothing sold in the last 6 months, so there is no selling price to go on.');

  const raw: { key: PriceOptionKey; label: string; price: number; basis: string }[] = [];
  if (cost !== null) {
    raw.push({ key: 'breakeven', label: 'Break even (your cost)', price: cost, basis: `Exactly what it cost you (${$(cost)}), rounded up to the penny: no profit, but no loss. For parts the market will not pay their cost for.` });
    raw.push({ key: 'floor', label: 'Floor', price: cost / (1 - FLOOR_MARGIN), basis: `Your cost of ${$(cost)} plus enough for a ${Math.round(FLOOR_MARGIN * 100)}% margin. Going lower gives away profit.` });
  }
  if (soldAvg !== null) {
    const quick = Math.max(soldAvg * (1 - QUICK_DISCOUNT), soldMin ?? 0);
    raw.push({ key: 'quick', label: 'Quick sale', price: quick, basis: `${Math.round(QUICK_DISCOUNT * 100)}% under what it has been selling for (${$(soldAvg)} on average), to be among the cheapest.` });
    raw.push({ key: 'market', label: 'In line with the market', price: soldAvg, basis: `What it has actually been selling for: ${$(soldAvg)} on average over 6 months${soldMin !== null && soldMax !== null ? `, from ${$(soldMin)} to ${$(soldMax)}` : ''}.` });
    const high = stockAvg !== null && stockAvg > soldAvg ? Math.min(stockAvg, soldAvg * PATIENT_CEILING) : soldAvg * 1.1;
    raw.push({
      key: 'patient',
      label: 'Patient',
      price: high,
      basis:
        stockAvg !== null && stockAvg > soldAvg
          ? `Between what it sells for and what sellers are asking now (${$(stockAvg)}), for when you can wait for a buyer.`
          : `A little (10%) above what it sells for, for when you can wait for a buyer.`,
    });
  }
  if (cost !== null) {
    raw.push({ key: 'cost_plus', label: `Cost plus ${Math.round(TARGET_MARGIN * 100)}%`, price: cost / (1 - TARGET_MARGIN), basis: `Your cost of ${$(cost)} with a ${Math.round(TARGET_MARGIN * 100)}% margin, whatever the market does.` });
  }

  // Which to put first: it depends on how fast it moves.
  let pick = null as PriceOptionKey | null;
  if (soldAvg !== null && measure) {
    if (measure.state === 'no_recent_sales') pick = 'quick';
    else if (measure.state === 'none_for_sale' || (measure.sellThrough ?? 0) >= 0.5) pick = 'patient';
    else if ((measure.sellThrough ?? 0) >= 0.2) pick = 'market';
    else pick = 'quick';
  } else if (cost !== null) pick = 'cost_plus';

  // Rather than recommend a price that loses money, recommend breaking even.
  const pickedRaw = raw.find((r) => r.key === pick);
  if (pick && pick !== 'breakeven' && pickedRaw && cost !== null && nicePrice(pickedRaw.price) < cost) pick = 'breakeven';

  const options: PriceOption[] = raw.map((r) => {
    // Break even rounds UP, so the price is never a fraction of a penny under cost.
    const price = r.key === 'breakeven' ? Math.max(0.01, Math.ceil(r.price * 100 - 1e-9) / 100) : nicePrice(r.price);
    const warnings: string[] = [];
    // Stock added with no purchase price is free: every penny of the price is profit.
    const costForProfit = i.avgCost === 0 ? 0 : cost;
    const profit = costForProfit !== null ? price - costForProfit : null;
    if (profit !== null && profit < 0) warnings.push(`Below your cost: loses ${$(-profit)} on every piece.`);
    else if (r.key !== 'breakeven' && cost !== null && profit !== null && profit / price < FLOOR_MARGIN - 0.0005) warnings.push(`Thin: under a ${Math.round(FLOOR_MARGIN * 100)}% margin.`);
    if (stockAvg !== null && price > stockAvg * 1.2 && r.key !== 'cost_plus') warnings.push(`Well above the ${$(stockAvg)} sellers are asking on BrickLink now.`);
    return {
      key: r.key,
      label: r.label,
      price: money4(price),
      profitEach: profit === null ? null : money4(profit),
      marginPct: profit === null ? null : Number(((profit / price) * 100).toFixed(1)),
      vsSoldPct: soldAvg === null ? null : Number((((price - soldAvg) / soldAvg) * 100).toFixed(1)),
      basis: r.basis,
      warnings,
      recommended: r.key === pick,
    };
  });
  options.sort((a, b) => Number(a.price) - Number(b.price));

  return {
    options,
    notes,
    facts: {
      avgCost: cost === null ? null : money4(cost),
      soldAvg: soldAvg === null ? null : money4(soldAvg),
      soldMin: soldMin === null ? null : money4(soldMin),
      soldMax: soldMax === null ? null : money4(soldMax),
      soldPieces: sold ? sold.quantity : null,
      soldLots: sold ? sold.lots : null,
      forSaleAvg: stockAvg === null ? null : money4(stockAvg),
      forSaleMin: stockMin === null ? null : money4(stockMin),
      forSalePieces: forSale ? forSale.quantity : null,
      sellThroughPct: measure && measure.sellThrough !== null ? Math.round(measure.sellThrough * 100) : null,
      soldPerMonth: measure ? (measure.soldPerMonth >= 10 ? Math.round(measure.soldPerMonth) : Number(measure.soldPerMonth.toFixed(1))) : null,
      yourMonthsOfStock: measure && measure.soldPerMonth > 0 && i.qty > 0 ? Number((i.qty / measure.soldPerMonth).toFixed(1)) : null,
      marketAge: usable && m ? describeAge(m.fetchedAt, i.now) : null,
      stale: !!(usable && m && (i.now ?? new Date()).getTime() - new Date(m.fetchedAt).getTime() > MARKET_STALE_DAYS * 86_400_000),
      current: i.current === null ? null : money4(i.current),
    },
  };
}

/** A base price at a price level (a percentage added to it, negative for a discount), rounded to the penny, never under 1p. */
export function priceAtLevel(base: number, percent: number): number {
  return Math.max(0.01, Math.round(base * (1 + percent / 100) * 100) / 100);
}
